import { Component, ElementRef, Inject, inject, Input, OnChanges, OnDestroy, QueryList, Renderer2, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MessageService, MessageThread } from '@nano/common/message';
import { RoutingService } from '@nano/common/routing';
import { CONFIG } from '@nano/core/config';
import { ErrorService } from '@nano/core/error';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { iif, of, Subject } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';
import { ReplyToMessageThreadRequest } from './message-thread-details';
import { MessageThreadDetailsService } from './message-thread-details.service';

@Component({
  selector: 'app-message-thread-details',
  templateUrl: './message-thread-details.component.html',
  styleUrls: ['./message-thread-details.component.less'],
  standalone: false
})
export class MessageThreadDetailsComponent implements OnChanges, OnDestroy {
  @Input() id: string;

  @ViewChildren('messages') messages: QueryList<ElementRef>;

  form: UntypedFormGroup;
  messageThread: MessageThread;
  sendMessageButtonLoading$ = new Subject<boolean>();
  messageAttachments = [];
  uploading = false;

  private hasIncomingMessage: boolean;
  private destroy$: Subject<void> = new Subject();

  private config = inject(CONFIG);

  constructor(
    private formBuilder: UntypedFormBuilder,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private routingService: RoutingService,
    private messageService: MessageService,
    private messageThreadDetailsService: MessageThreadDetailsService,
    private errorService: ErrorService,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {
    this.form = this.formBuilder.group({
      body: [null, [Validators.required, Validators.maxLength(this.config.messageBodyLength)]],
      attachmentIds: [null]
    });

    this.initIncomingMessageListener();
  }

  ngOnChanges(): void {
    this.form.reset();
    this.getMessageThreadDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sendMessage(): void {
    this.messageThreadDetailsService.replyToMessageThread(this.id, this.form.value as ReplyToMessageThreadRequest).subscribe({
      next: () => {
        this.form.reset();

        const nativeElement = this.elementRef.nativeElement as HTMLElement;
        const editableDiv = nativeElement.querySelector('.app-send-message-input');
        if (editableDiv) {
          this.renderer.setProperty(editableDiv, 'innerHTML', '');
        }

        this.messageAttachments = [];
        this.getMessageThreadDetails();
      },
      error: (error: unknown) => this.errorService.handleRequestError(error)
    });
  }

  removeMessageThread(): void {
    this.messageThreadDetailsService.removeMessageThread(this.id).subscribe({
      next: () => this.routingService.navigateToMessages(),
      error: (error: unknown) => this.errorService.handleRequestError(error)
    });
  }

  fileListUpdated(files: NzUploadFile[]): void {
    this.form.get('attachmentIds').setValue(files.map((file) => file.id as string));
  }

  fileUploading(uploading: boolean): void {
    this.uploading = uploading;
  }

  private initIncomingMessageListener(): void {
    this.messageService.unreadMessageThreads$.pipe(takeUntil(this.destroy$)).subscribe((unreadMessageThreads) => {
      if (!this.hasIncomingMessage && unreadMessageThreads.findIndex((messageThread) => messageThread.id === this.id) > -1) {
        this.hasIncomingMessage = true;
        this.getMessageThreadDetails();
      }
    });
  }

  private getMessageThreadDetails(): void {
    this.messageThreadDetailsService
      .getMessageThreadDetails(this.id)
      .pipe(
        mergeMap((messageThread: MessageThread) =>
          iif(() => messageThread.isRead, of(messageThread), this.messageThreadDetailsService.markMessageThreadAsRead(this.id).pipe(map(() => messageThread)))
        )
      )
      .subscribe((messageThread: MessageThread) => {
        this.messageThread = messageThread;
        this.hasIncomingMessage = false;
      });
  }
}
